import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

import { ARCHETYPE_DEFAULT_ACCENT } from '../../domainTypes/ddr';
import { CiqDialog } from '../ciq-dialog/ciq-dialog';
import { RoleChip } from '../role-chip/role-chip';
import { AliceOnboardingService } from '../../services/alice-onboarding.service';
import { AuthService } from '../../services/auth.service';
import { DdrRoomStreamService } from '../../services/ddr-room-stream.service';

/** "Meet your team" (2026-09-15): the first thing a new user sees after
 *  signup. Explains what is about to happen, then hands them to Alice, who
 *  runs the hiring script. Shows once per browser while the script is
 *  unfinished; the sidebar nudge takes over after that. */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CiqDialog, RoleChip],
    selector: 'app-meet-your-team-dialog',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrl: './meet-your-team-dialog.css',
    templateUrl: './meet-your-team-dialog.html',
})
export class MeetYourTeamDialog {
    private readonly auth = inject(AuthService);
    private readonly onboarding = inject(AliceOnboardingService);
    private readonly stream = inject(DdrRoomStreamService);
    private readonly router = inject(Router);

    /** The app consent page is a standalone screen someone lands on from an app;
     *  covering it (and offering to navigate away) loses their place. */
    private readonly onConsentPage = toSignal(
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(e => e.urlAfterRedirects.startsWith('/app-connect/'))
        ),
        { initialValue: this.router.url.startsWith('/app-connect/') }
    );
    // This component lives at app-root, outside the onboarding wizard's own
    // route -- so it mounts there too. The wizard re-mints auth state (and
    // with it ddrEnabled/onboarding.active) as soon as the workspace exists,
    // which is before the user has clicked through to /chat, so without this
    // the dialog pops up over the wizard's own "you're all set" step with no
    // app behind it. Tracks navigation rather than reading router.url once,
    // the same pattern auth-layout.ts uses for isSignIn.
    private readonly onOnboardingRoute = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(() => this.router.url.startsWith('/onboarding')),
            startWith(this.router.url.startsWith('/onboarding'))
        ),
        { initialValue: this.router.url.startsWith('/onboarding') }
    );

    readonly isOpen = computed(
        () =>
            !this.onConsentPage() &&
            this.auth.state().status === 'authenticated' &&
            this.auth.ddrEnabled() &&
            this.onboarding.active() &&
            !this.onboarding.introSeen() &&
            !this.onOnboardingRoute()
    );
    readonly roleTitle = this.onboarding.roleTitle;
    /** Alice as hired on this account, for her picture and name; the roster
     *  may not have landed yet on a brand-new account, so every read is
     *  optional in the template. */
    readonly alice = computed(() => Array.from(this.stream.personas().values()).find(p => p.archetype === 'talent') ?? null);
    /** The three roles the script hires, in the order Alice asks for them. */
    readonly roles = [
        {
            archetype: 'assistant',
            accent: ARCHETYPE_DEFAULT_ACCENT.assistant,
            icon: 'envelope',
            line: 'Handles your email and calendar and gets you ready for the day.',
        },
        {
            archetype: 'engineer',
            accent: ARCHETYPE_DEFAULT_ACCENT.engineer,
            icon: 'code',
            line: 'Builds apps and tools when you need them.',
        },
        {
            archetype: 'pm',
            accent: ARCHETYPE_DEFAULT_ACCENT.pm,
            icon: 'list-check',
            line: 'Keeps track of what everyone is working on.',
        },
    ] as const;

    // noinspection JSUnusedLocalSymbols
    private readonly _loadOnLogin = effect(() => {
        const live = this.auth.state().status === 'authenticated' && this.auth.ddrEnabled();
        if (live && !untracked(() => this.onboarding.loaded())) {
            void this.onboarding.refresh();
        }
    });

    /** The status read above races provisioning: on a brand-new account the
     *  roster read is what creates the onboarding progress row (list_personas
     *  starts the script when it has to create Alice), and it runs in parallel
     *  with that first fetch. Lose the race and the status loads as inactive,
     *  stays cached as loaded, and this dialog never opens. Re-read whenever
     *  the roster changes: it is the one signal that always moves once
     *  provisioning lands, and during the script every hire moves it too, so
     *  the read that closes the script (the third hire) lands without a
     *  reload. The service's generation guard drops a stale answer. */
    private rosterReads = 0;
    private rosterReadsUser: number | null = null;

    // noinspection JSUnusedLocalSymbols
    private readonly _refetchOnRoster = effect(() => {
        const roster = this.stream.personas();
        const state = this.auth.state();
        const live = state.status === 'authenticated' && this.auth.ddrEnabled();
        if (!live || roster.size === 0) {
            return;
        }
        // This component lives outside the router outlet and survives a
        // sign-out, so the counter is per signed-in user or account B would
        // inherit account A's "already read once" and lose its own
        // post-provisioning read.
        const userId = state.user?.user_id ?? null;
        if (userId !== this.rosterReadsUser) {
            this.rosterReadsUser = userId;
            this.rosterReads = 0;
        }
        // The first read after the roster lands always runs: the login-time
        // read can beat provisioning and cache inactive. After that, re-read
        // only while the script is active; an inactive script plus a roster
        // change is a rename or a later hire, and re-fetching forever for
        // every user was the cost of not stopping here.
        if (this.rosterReads > 0 && this.onboarding.loaded() && !this.onboarding.active()) {
            return;
        }
        this.rosterReads += 1;
        untracked(() => void this.onboarding.refresh());
    });

    meetAlice(): void {
        this.onboarding.markIntroSeen();
        const alice = Array.from(this.stream.personas().values()).find(p => p.archetype === 'talent');
        if (alice) {
            void this.router.navigate(['/chat/ddr/dm', alice.id]);
        }
    }

    later(): void {
        this.onboarding.markIntroSeen();
    }

    /** Escape closes the dialog through the primitive; treat it as Later. */
    onOpenChange(open: boolean): void {
        if (!open && this.isOpen()) {
            this.later();
        }
    }
}
